import {useRef, useState} from 'react';
import {FmlxRadioButton, FmlxIcon, FmlxPopover} from 'fmlx-common-ui';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import withIntl from '../lib/withIntl';
import './TouchProbeGuideImage.scss';

const TouchProbeImg = styled.img`
  max-width: 80%;
  padding: 2px;
  cursor: ${(props) => (props.isToggleable ? 'pointer' : 'default')};
  border-radius: 15px;
  border: 3px solid !important;
  border: ${(props) => (props.isSelected ? '3px solid #006af5 !important' : '3px solid transparent !important')};
`;

const TouchProbeOptionsEnum = {
  NOT_SELECTED: -1,
  NOT_EXTENDED: 1,
  EXTENDED: 2,
};

function TouchProbeGuideImage({onToggle, intl}) {
  const [selectedOption, setSelectedOption] = useState(TouchProbeOptionsEnum.NOT_SELECTED);

  const toggleOptions = (value) => {
    if (onToggle) {
      setSelectedOption(value);
      onToggle(value - 1); // subtracted by one to get the index
    }
  };

  const getOptionComponent = (touchProbeOption) => {
    const optionLabel = touchProbeOption === TouchProbeOptionsEnum.EXTENDED ? 'extended' : 'retracted';

    return (
      <span key={touchProbeOption}>
        <TouchProbeImg
          alt={`Touch probe ${optionLabel}`}
          src={`./images/touch-probe/touch-probe-${optionLabel}.png`}
          isSelected={selectedOption === touchProbeOption && onToggle}
          isToggleable={onToggle}
          onClick={() => toggleOptions(touchProbeOption)}
        />
        <div className="touch-probe-guide-image__content-option">
          {onToggle ? (
            <FmlxRadioButton
              selectedIndex={selectedOption === touchProbeOption ? 0 : -1}
              onChange={() => toggleOptions(touchProbeOption)}
              items={[
                {
                  content: intl?.formatMessage({id: `touchProbeGuideImage.image.togglable.${optionLabel}.label`}),
                  hint: intl?.formatMessage({id: `touchProbeGuideImage.image.togglable.${optionLabel}.description`}),
                  type: 'information',
                },
              ]}
            />
          ) : (
            intl?.formatMessage({id: `touchProbeGuideImage.image.${optionLabel}.label`})
          )}
        </div>
      </span>
    );
  };

  return (
    <div className="touch-probe-guide">
      {getOptionComponent(TouchProbeOptionsEnum.EXTENDED)}
      {getOptionComponent(TouchProbeOptionsEnum.NOT_EXTENDED)}
    </div>
  );
}

TouchProbeGuideImage.propTypes = {
  onToggle: PropTypes.func,
  intl: PropTypes.any.isRequired,
};

TouchProbeGuideImage.defaultProps = {
  onToggle: null,
};

export const TouchProbeGuideTooltip = withIntl((props) => {
  const {iconStyle, intl, placement} = props;

  const [anchorEl, setAnchorEl] = useState(null);

  const iconTooltipRef = useRef(null);
  const tooltipRef = useRef(null);

  const getPosition = () => {
    switch (placement) {
      case 'left':
        return {
          anchorOrigin: {vertical: 'top', horizontal: 'left'},
          transformOrigin: {vertical: 'top', horizontal: 'right'},
        };
      case 'right':
        return {
          anchorOrigin: {vertical: 'top', horizontal: 'right'},
          transformOrigin: {vertical: 'top', horizontal: 'left'},
        };
      case 'top':
        return {
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
          transformOrigin: {vertical: 'bottom', horizontal: 'center'},
        };
      case 'bottom':
      default:
        return {
          anchorOrigin: {vertical: 'bottom', horizontal: 'center'},
          transformOrigin: {vertical: 'top', horizontal: 'center'},
        };
    }
  };

  return (
    <span className="touch-probe-guide-tooltip">
      <span
        ref={iconTooltipRef}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        onKeyDown={(e) => setAnchorEl(e.currentTarget)}
        style={iconStyle}
        className="icon-tooltip"
        role="button"
        tabIndex={0}
      >
        <FmlxIcon name="InfoOutline" size="xs" />
      </span>
      <FmlxPopover
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={placement}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getPosition()}
      >
        <div ref={tooltipRef} className="touch-probe-guide-tooltip-popover">
          <p className="label-tooltip">{intl?.formatMessage({id: 'touchProbeGuideImage.headerText'})}</p>
          <TouchProbeGuideImage intl={intl} />
          <br />
        </div>
      </FmlxPopover>
    </span>
  );
});

TouchProbeGuideTooltip.propTypes = {
  iconStyle: PropTypes.object,
  // tooltipStyle: PropTypes.object,
  intl: PropTypes.any.isRequired,
  placement: PropTypes.string,
};

TouchProbeGuideTooltip.defaultProps = {
  iconStyle: {},
  // tooltipStyle: {},
  placement: 'bottom',
};

export default withIntl(TouchProbeGuideImage);
